import { INVALID_FIELDS } from '@/validations';
import dayjs from 'dayjs';
import z, {
  ZodArray,
  ZodDiscriminatedUnion,
  ZodEffects,
  ZodIntersection,
  ZodNullable,
  ZodObject,
  ZodOptional,
  ZodTypeAny,
  ZodUnion,
} from 'zod';

export const zRequiredMessage = INVALID_FIELDS.REQUIRED;
export const zWrongFormatMessage = INVALID_FIELDS.WRONG_FORMAT;
export const zLengthNotMatchMessage = INVALID_FIELDS.LENGTH_NOT_MATCH;

export const zRequiredString = z
  .string({
    required_error: zRequiredMessage,
    invalid_type_error: zRequiredMessage,
  })
  .min(1, zRequiredMessage);

export const zString = z.string().nullish();

export const zEmail = z.preprocess(
  val => (val === '' || val === null ? undefined : val),
  z.string().email({ message: INVALID_FIELDS.EMAIL }).optional(),
);

export const zNumber = z.string().refine(value => !isNaN(Number(value)));
export const zPositiveNumber = zNumber.refine(
  value => Number(value) > 0,
  'Must be > 0',
);
export const zNonNegativeNumber = zNumber.refine(
  value => Number(value) >= 0,
  'Must be >= 0',
);
export const zRequiredPositiveNumber = zRequiredString.refine(
  value => Number(value) > 0,
  'Must be > 0',
);
export const zRequiredNonNegativeNumber = zRequiredString.refine(
  value => Number(value) >= 0,
  'Must be >= 0',
);

export const zRequiredDate = z
  .custom<dayjs.Dayjs>(val => dayjs.isDayjs(val), zRequiredMessage)
  .transform(val => dayjs(val));

export const zDate = zRequiredDate.nullish();

/* -------------------------------------------------------------------------- */
/*                              Helper functions                              */
/* -------------------------------------------------------------------------- */

/**
 * Transforms a path string from dot notation for arrays to bracket notation.
 * @param dotNotationString The string to convert (e.g., 'items.0.name').
 * @returns The converted string (e.g., 'items[0].name').
 */
export const dotToBracket = (dotNotationString: string): string => {
  // Use a regular expression to find all instances of a dot followed by one or more digits.
  // The 'g' flag ensures all occurrences are replaced, not just the first.
  // \.   -> matches a literal dot
  // \d+  -> matches one or more digits
  const regex = /\.\d+/g;

  // The replace method can take a function as the second argument.
  // This function is called for each match found.
  // 'match' will be the matched string, e.g., '.0', '.1', '.23'
  return dotNotationString.replace(regex, match => {
    // Remove the leading dot from the match to get just the number.
    const _number = match.substring(1);
    // Return the number wrapped in brackets.
    return `[]`;
  });
};

// Helper function to unwrap ZodEffects
export const unwrapZodEffects = (schema: ZodTypeAny): ZodTypeAny => {
  if (schema instanceof ZodEffects) {
    return unwrapZodEffects(schema._def.schema);
  }
  return schema;
};

// Helper function to get the base schema from union types
export const getBaseSchemaFromUnion = (
  schema: ZodUnion<any>,
): ZodTypeAny | undefined => {
  const options = schema.options;

  // Try to find an object schema in the union
  for (const option of options) {
    const unwrapped = unwrapZodEffects(option);
    if (unwrapped instanceof ZodObject) {
      return unwrapped;
    }
  }

  // If no object found, return the first option
  return options[0] ? unwrapZodEffects(options[0]) : undefined;
};

/**
 * Traverses a Zod schema and returns a map of all field paths to their corresponding Zod schemas.
 *
 * @param schema The Zod schema to analyze.
 * @returns An object where keys are field path patterns (e.g., 'items[].name') and
 *          values are the ZodTypeAny schemas for those fields.
 */
export const getFieldSchemas = (
  schema: ZodTypeAny,
): { [path: string]: ZodTypeAny } => {
  // We use a Map for better performance and to avoid prototype pollution.
  const fieldSchemas = new Map<string, ZodTypeAny>();
  const visited = new Set<ZodTypeAny>();

  const processSchema = (currentSchema: ZodTypeAny, currentPath: string) => {
    const unwrappedSchema = unwrapZodEffects(currentSchema);

    if (visited.has(unwrappedSchema)) return;
    visited.add(unwrappedSchema);

    if (unwrappedSchema instanceof ZodObject) {
      const shape = unwrappedSchema.shape;
      for (const [key, fieldSchema] of Object.entries(shape)) {
        const fieldPath = currentPath ? `${currentPath}.${key}` : key;
        const unwrappedField = unwrapZodEffects(fieldSchema as ZodTypeAny);

        // Add the schema for this specific field path to the map.
        fieldSchemas.set(fieldPath, unwrappedField);

        // Recurse into the nested schema.
        processSchema(unwrappedField, fieldPath);
      }
    } else if (unwrappedSchema instanceof ZodArray) {
      const arrayElementPath = `${currentPath}[]`;
      const elementSchema = unwrappedSchema.element;

      // Add the schema for the array element pattern.
      fieldSchemas.set(arrayElementPath, unwrapZodEffects(elementSchema));

      // Recurse into the array's element schema.
      processSchema(elementSchema, arrayElementPath);
    } else if (unwrappedSchema instanceof ZodUnion) {
      const baseSchema = getBaseSchemaFromUnion(unwrappedSchema);
      if (baseSchema) {
        // We traverse into the most likely candidate (an object, or the first option).
        processSchema(baseSchema, currentPath);
      }
    } else if (unwrappedSchema instanceof ZodIntersection) {
      // Process both sides of the intersection, as they will add fields to the same object path.
      processSchema(unwrappedSchema._def.left, currentPath);
      processSchema(unwrappedSchema._def.right, currentPath);
    } else if (
      unwrappedSchema instanceof ZodOptional ||
      unwrappedSchema instanceof ZodNullable
    ) {
      // Traverse into the wrapped schema.
      processSchema(unwrappedSchema.unwrap(), currentPath);
    }

    visited.delete(unwrappedSchema);
  };

  processSchema(schema, '');

  // Convert the Map to a plain object for easier consumption.
  return Object.fromEntries(fieldSchemas.entries());
};

/**
 * Check if a field is required in a discriminated union schema
 * Returns true if the field is required in ANY of the union options where it exists
 */
export const isFieldRequiredInDiscriminatedUnion = (
  schema: ZodDiscriminatedUnion<any, any>,
  fieldPath: string,
): boolean => {
  const options = Array.from(schema.options.values());

  // Check if the field is required in any option where it exists
  for (const option of options) {
    const unwrappedOption = unwrapZodEffects(option as ZodTypeAny);

    if (unwrappedOption instanceof ZodObject) {
      const shape = unwrappedOption.shape;
      const fieldSchema = shape[fieldPath];

      if (fieldSchema) {
        const unwrappedField = unwrapZodEffects(fieldSchema as ZodTypeAny);

        // If the field exists and is required (not optional/nullable), return true
        if (
          !(unwrappedField instanceof ZodOptional) &&
          !(unwrappedField instanceof ZodNullable)
        ) {
          return true;
        }
      }
    }
  }

  // If field doesn't exist in any option or is optional in all options where it exists
  return false;
};

/**
 * Check if a field is required given a specific discriminator value
 */
export const isFieldRequiredForDiscriminator = (
  schema: ZodDiscriminatedUnion<any, any>,
  fieldPath: string,
  discriminatorValue: string,
): boolean => {
  const option = schema.options.get(discriminatorValue);
  if (!option) return false;

  const unwrappedOption = unwrapZodEffects(option);
  if (!(unwrappedOption instanceof ZodObject)) return false;

  const shape = unwrappedOption.shape;
  const fieldSchema = shape[fieldPath];

  if (!fieldSchema) return false;

  const unwrappedField = unwrapZodEffects(fieldSchema as ZodTypeAny);

  return (
    !(unwrappedField instanceof ZodOptional) &&
    !(unwrappedField instanceof ZodNullable)
  );
};

export const isFieldRequired = (
  schema: ZodTypeAny,
  fieldPath: string,
): boolean => {
  if (schema instanceof ZodDiscriminatedUnion) {
    // Always check if field is required in any option
    return isFieldRequiredInDiscriminatedUnion(schema, fieldPath);
  }

  // For other schema types, unwrap effects and use the original logic
  const fieldSchemas = getFieldSchemas(schema);
  const fieldSchema = fieldSchemas[dotToBracket(fieldPath)];

  if (!fieldSchema) {
    return false;
  }

  return (
    !(fieldSchema instanceof ZodOptional) &&
    !(fieldSchema instanceof ZodNullable)
  );
};

/**
 * Recursively traverses an object or array and converts all `null` values to `undefined`.
 * @param data The object or array to process.
 * @returns A new object or array with nulls converted to undefined.
 */
export const convertNullsToUndefined = <T>(data: T): T => {
  // Base case: if data is null, convert it to undefined.
  if (data === null) {
    return undefined as T;
  }

  // If it's an array, recursively process each item.
  if (Array.isArray(data)) {
    return data.map(item => convertNullsToUndefined(item)) as T;
  }

  // If it's a plain object, recursively process each value.
  if (typeof data === 'object') {
    // Create a new object to hold the results.
    const newObj = {} as T;
    for (const key in data) {
      // Ensure we only process own properties.
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        newObj[key] = convertNullsToUndefined(data[key]);
      }
    }
    return newObj;
  }

  // For all other types (string, number, boolean, undefined), return them as is.
  return data;
};
